import type { LegalSection } from '../legalDocuments.constants'
import { WC2026_ORG_BILLING_TERMS_SECTION } from '../../../shared/constants/wc2026OrgBillingDisclosure.constants'

const FR_PRIVACY_POLICY_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Introduction',
    paragraphs: [
      'Zporti (« nous », « notre ») exploite l’application mobile et web Zporti (le « Service »). La présente Politique de confidentialité décrit comment nous collectons, utilisons et partageons les informations lorsque vous utilisez le Service.',
      'En utilisant le Service, vous acceptez cette politique. Si vous n’êtes pas d’accord, veuillez ne pas utiliser le Service.',
    ],
  },
  {
    title: 'Informations que nous collectons',
    paragraphs: [
      'Informations de compte et de profil : telles que nom, adresse e-mail, nom d’utilisateur, choix d’avatar ou d’image de profil, et autres détails que vous fournissez lors de la création ou de la mise à jour de votre compte.',
      'Données d’authentification : nous utilisons l’authentification Supabase et pouvons prendre en charge la connexion par e-mail, des fournisseurs OAuth (par exemple Google ou Facebook) et des sessions invitées selon la plateforme et la version.',
      'Contenu que vous créez : telles que prédictions sportives, défis, adhésions à des groupes, connexions d’amis, invitations et messages que vous envoyez via les formulaires de contact ou de support.',
      'Informations d’utilisation et sur l’appareil : telles que événements de l’application, localisation approximative dérivée de l’adresse IP le cas échéant, type d’appareil, système d’exploitation et données de diagnostic pour maintenir le Service sûr et fiable.',
    ],
  },
  {
    title: 'Cookies, analyses et consentement',
    paragraphs: [
      'Sur le web, nous utilisons des cookies ou un stockage essentiel pour l’authentification, la sécurité et le fonctionnement de base de l’application.',
      'Les analyses non essentielles sont désactivées par défaut jusqu’à ce que vous donniez votre consentement. Vous pouvez modifier cette préférence ultérieurement depuis les contrôles de confidentialité dans l’application.',
      'Si elles sont activées, les prestataires d’analyses peuvent traiter des événements d’utilisation pseudonymisés et des métadonnées de compte limitées pour nous aider à améliorer la qualité et la fiabilité du produit.',
    ],
  },
  {
    title: 'Communications marketing',
    paragraphs: [
      'Les e-mails marketing sont sur opt-in. Nous n’envoyons des communications marketing que lorsque vous y consentez activement.',
      'Vous pouvez retirer votre consentement marketing à tout moment depuis les contrôles de confidentialité.',
      'Lorsque vous créez un compte, nous enregistrons la date et la version de la politique pour le consentement marketing optionnel et pour votre acceptation de nos Conditions d’utilisation et de notre Politique de confidentialité, afin de pouvoir démontrer ce que vous avez accepté si nécessaire.',
    ],
  },
  {
    title: 'Comment nous utilisons les informations',
    paragraphs: [
      'Nous utilisons les informations ci-dessus pour fournir et améliorer le Service, authentifier les utilisateurs, personnaliser votre expérience, gérer les défis et les classements, envoyer des avis liés au service, analyser l’utilisation de manière agrégée, prévenir la fraude et les abus, et respecter la loi.',
    ],
  },
  {
    title: 'Comment nous partageons les informations',
    paragraphs: [
      'Nous partageons des informations avec des prestataires qui nous aident à héberger le Service, envoyer des e-mails, traiter les analyses ou effectuer des fonctions similaires, sous réserve de garanties appropriées.',
      'Nous pouvons divulguer des informations si la loi l’exige, pour protéger nos droits et ceux des utilisateurs, ou dans le cadre d’une fusion ou d’une cession d’actifs (avec notification lorsque requis).',
      'D’autres utilisateurs peuvent voir les informations que vous choisissez de rendre visibles dans l’application (par exemple nom affiché, avatar et activité liée aux défis) selon les fonctionnalités du produit.',
    ],
  },
  {
    title: 'Conservation',
    paragraphs: [
      'Nous conservons les informations tant que votre compte est actif et aussi longtemps que nécessaire pour fournir le Service, respecter nos obligations légales, résoudre les litiges et faire respecter nos accords.',
    ],
  },
  {
    title: 'Vos choix et vos droits',
    paragraphs: [
      'Selon votre lieu de résidence, vous pouvez avoir le droit d’accéder, de corriger, de supprimer ou d’exporter vos informations personnelles, ou de vous opposer à certains traitements ou de les limiter.',
      'Les contrôles de confidentialité dans l’application incluent les demandes de suppression de compte, les contrôles de consentement marketing et le téléchargement d’export de données lorsque disponible.',
      'Vous pouvez également nous contacter à info@zporti.com. Nous pouvons avoir besoin de vérifier votre demande avant d’y répondre.',
    ],
  },
  {
    title: 'Enfants',
    paragraphs: [
      'Le Service ne s’adresse pas aux enfants de moins de 13 ans (ou à l’âge minimum requis dans votre région). Nous ne collectons pas sciemment d’informations personnelles auprès d’enfants.',
    ],
  },
  {
    title: 'Modifications',
    paragraphs: [
      'Nous pouvons mettre à jour cette Politique de confidentialité de temps à autre. Nous publierons la version mise à jour sur cette page et mettrons à jour la date d’effet lorsque nous le ferons.',
    ],
  },
  {
    title: 'Contact',
    paragraphs: [
      'Questions concernant cette politique : info@zporti.com',
    ],
  },
]

const FR_DATA_DELETION_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Aperçu',
    paragraphs: [
      'Vous pouvez demander la suppression des données personnelles associées à votre compte Zporti. Cette page décrit comment formuler cette demande et ce à quoi vous attendre.',
    ],
  },
  {
    title: 'Comment demander la suppression',
    paragraphs: [
      'Utilisez les contrôles de confidentialité dans l’application pour demander la suppression du compte depuis votre session authentifiée.',
      'Ou envoyez un e-mail à info@zporti.com depuis l’adresse e-mail associée à votre compte Zporti (ou indiquez clairement l’e-mail du compte et tout autre détail permettant de localiser votre profil).',
      'Utilisez l’objet « Data deletion request ». Nous pouvons vous demander de confirmer votre identité avant de traiter la demande.',
      'Vous pouvez également télécharger une copie de vos données de compte depuis les contrôles de confidentialité dans l’application.',
    ],
  },
  {
    title: 'Ce que nous supprimons',
    paragraphs: [
      'Après vérification, nous supprimerons ou anonymiserons les données personnelles liées à votre compte lorsque leur conservation n’est pas requise à des fins légales, de sécurité ou de résolution de litiges.',
      'Certaines informations peuvent rester dans des sauvegardes pendant une période limitée jusqu’à leur rotation, ou lorsque la loi exige leur conservation.',
    ],
  },
  {
    title: 'Délais',
    paragraphs: [
      'Nous visons à répondre aux demandes vérifiées dans un délai raisonnable, et dans tout délai exigé par la loi applicable dans votre région.',
    ],
  },
  {
    title: 'Contact',
    paragraphs: [
      'Questions sur la suppression des données : info@zporti.com',
    ],
  },
]

const FR_TERMS_OF_SERVICE_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Accord',
    paragraphs: [
      'Les présentes Conditions d’utilisation (« Conditions ») régissent votre accès et votre utilisation de l’application mobile et web Zporti (le « Service ») exploitée par Zporti (« nous », « notre »).',
      'En créant un compte, en accédant ou en utilisant le Service, vous acceptez ces Conditions. Si vous n’êtes pas d’accord, n’utilisez pas le Service.',
      'Lors de votre inscription, vous confirmez l’acceptation de ces Conditions et de notre Politique de confidentialité, distinctement de tout consentement optionnel aux e-mails marketing.',
    ],
  },
  {
    title: 'Le Service',
    paragraphs: [
      'Zporti propose une expérience sociale de prédictions et de défis sportifs : vous pouvez créer ou rejoindre des défis avec des amis, soumettre des prédictions, consulter les résultats et les classements, et utiliser les fonctionnalités connexes que nous pouvons ajouter ou modifier au fil du temps.',
      'Nous pouvons modifier, suspendre ou interrompre certaines parties du Service avec un préavis raisonnable lorsque c’est possible. Nous ne garantissons pas un fonctionnement ininterrompu ou exempt d’erreurs.',
    ],
  },
  {
    title: 'Éligibilité et comptes',
    paragraphs: [
      'Vous devez respecter l’âge minimum requis dans votre région pour utiliser le Service (généralement au moins 13 ans, ou plus si la loi locale l’exige).',
      'Vous êtes responsable de l’exactitude des informations que vous fournissez et de la sécurité de vos identifiants de connexion. Informez-nous rapidement via le contact ci-dessous si vous suspectez un accès non autorisé à votre compte.',
    ],
  },
  {
    title: 'Utilisation acceptable',
    paragraphs: [
      'Vous acceptez de ne pas utiliser le Service de manière abusive. Par exemple, vous ne devez pas : enfreindre la loi ; harceler, abuser ou nuire à autrui ; tenter d’obtenir un accès non autorisé à nos systèmes ou aux comptes d’autres utilisateurs ; extraire des données ou surcharger le Service ; distribuer des logiciels malveillants ; tricher, manipuler ou perturber les défis ou les résultats ; ou utiliser le Service pour envoyer du spam ou des communications trompeuses.',
      'Nous pouvons enquêter et suspendre ou résilier les comptes qui enfreignent ces Conditions ou mettent le Service ou les utilisateurs en danger.',
    ],
  },
  {
    title: 'Contenu utilisateur',
    paragraphs: [
      'Vous conservez les droits sur le contenu que vous soumettez (telles que informations de profil, messages et activité dans les défis). Vous nous accordez une licence non exclusive pour héberger, stocker, afficher et traiter ce contenu selon les besoins pour exploiter et améliorer le Service.',
      'Vous êtes responsable du contenu que vous soumettez et confirmez que vous avez le droit de le partager. Nous pouvons supprimer le contenu qui enfreint ces Conditions ou la loi applicable.',
    ],
  },
  {
    title: WC2026_ORG_BILLING_TERMS_SECTION.title,
    paragraphs: [...WC2026_ORG_BILLING_TERMS_SECTION.paragraphs],
  },
  {
    title: 'Avertissements',
    paragraphs: [
      'Le Service est fourni « en l’état » et « selon disponibilité » dans toute la mesure permise par la loi. Les données sportives, cotes et résultats peuvent provenir de tiers ; nous ne garantissons pas leur exactitude ou leur actualité.',
      'La compétition amicale et les prédictions sont à des fins de divertissement. Rien dans le Service ne constitue un conseil en matière de jeux d’argent, financier ou professionnel lorsque une telle activité est réglementée ou interdite.',
    ],
  },
  {
    title: 'Limitation de responsabilité',
    paragraphs: [
      'Dans toute la mesure permise par la loi applicable, nous ne sommes pas responsables des dommages indirects, accessoires, spéciaux, consécutifs ou punitifs, ni de la perte de profits, de données ou de clientèle, découlant de votre utilisation du Service.',
      'Notre responsabilité totale pour les réclamations découlant du Service est limitée au plus élevé de (a) le montant que vous nous avez payé pour le Service au cours des douze mois précédant la réclamation ou (b) cent dollars américains (USD $100), sauf lorsque la loi n’autorise pas une telle limite.',
    ],
  },
  {
    title: 'Indemnisation',
    paragraphs: [
      'Vous acceptez de nous défendre et de nous indemniser, ainsi que nos affiliés, dirigeants et employés, contre les réclamations, dommages et dépenses (y compris les honoraires juridiques raisonnables) découlant de votre utilisation du Service, de votre contenu ou de votre violation de ces Conditions, dans la mesure permise par la loi.',
    ],
  },
  {
    title: 'Droit applicable et litiges',
    paragraphs: [
      'Ces Conditions sont régies par les lois applicables dans le lieu où Zporti exerce son activité, sans égard aux règles de conflit de lois, sauf lorsque les protections obligatoires des consommateurs dans votre pays en disposent autrement.',
      'Si vous êtes un consommateur, vous pouvez avoir le droit d’engager des litiges devant les tribunaux de votre lieu de résidence lorsque la loi l’exige.',
    ],
  },
  {
    title: 'Modifications',
    paragraphs: [
      'Nous pouvons mettre à jour ces Conditions de temps à autre. Nous publierons les Conditions mises à jour sur cette page. L’utilisation continue du Service après l’entrée en vigueur des modifications constitue l’acceptation des Conditions révisées lorsque la loi le permet.',
    ],
  },
  {
    title: 'Contact',
    paragraphs: [
      'Questions concernant ces Conditions : info@zporti.com',
    ],
  },
]

const FR_CREDITS_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Crédits tiers',
    paragraphs: [
      'Zporti utilise des ressources audio tierces sélectionnées pour les effets sonores dans l’application.',
      'Les ressources sonores proviennent de Freesound et sont utilisées sous licence CC0 (domaine public).',
    ],
  },
  {
    title: 'Attribution des icônes',
    paragraphs: [
      'Les icônes de sport proviennent de Flaticon et sont utilisées sous sa licence gratuite, qui exige une attribution.',
      'Basketball icons created by justicon — Flaticon: https://www.flaticon.com/free-icons/basketball',
      'Soccer ball icons created by DinosoftLabs — Flaticon: https://www.flaticon.com/free-icons/soccer-ball',
    ],
  },
  {
    title: 'Attribution audio',
    paragraphs: [
      'Freesound : https://freesound.org/',
      'Creative Commons CC0 1.0 : https://creativecommons.org/publicdomain/zero/1.0/',
    ],
  },
]

export const FR_LEGAL_SECTIONS = {
  privacy: FR_PRIVACY_POLICY_SECTIONS,
  dataDeletion: FR_DATA_DELETION_SECTIONS,
  terms: FR_TERMS_OF_SERVICE_SECTIONS,
  credits: FR_CREDITS_SECTIONS,
} as const
